import { useNotification } from 'admin';
import { useApiDocumentsV1DocumentsPartialUpdate } from 'api';
import { Form, type InferType, TextField, useForm, yup, yupResolver } from 'form';
import { Button, Dialog, DialogActions, DialogContent, DialogHeader } from 'ui';

const schema = yup.object().shape({
  alias: yup.string().required('Alias is required').max(40, 'Alias must be less than 40 characters'),
});

type FormValues = InferType<typeof schema>;

type UpdateAliasDialogProps = {
  open: boolean;
  alias: string | null;
  documentId: number;
  onClose: () => void;
  onSuccess?: () => void;
};

export const UpdateAliasDialog = ({ open, onClose, documentId, alias, onSuccess }: UpdateAliasDialogProps) => {
  const { open: openNotification } = useNotification();
  const { mutate: updateDocument, isPending } = useApiDocumentsV1DocumentsPartialUpdate({
    mutation: {
      onError: () => {
        openNotification?.({ message: 'Failed to update alias', type: 'error' });
      },
      onSuccess: () => {
        onClose();
        onSuccess?.();
        openNotification?.({ message: 'Alias updated', type: 'success' });
      },
    },
  });

  const form = useForm<FormValues>({
    defaultValues: {
      alias: alias ?? '',
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (values: FormValues) => {
    updateDocument({ data: values, id: documentId });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogHeader title="Update alias" onClose={onClose} />
      <Form {...form} onSubmit={onSubmit}>
        <DialogContent>
          <TextField
            name="alias"
            label="Alias"
            placeholder="Enter document alias"
            helperText="A user-friendly nickname or alternative title for the document"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={isPending}>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
